import type React from "react";
import { useContext } from "react";

import {
  faCalendarLinesPen,
  faClipboardMedical,
  faComments,
  faMessage,
  faSquareList,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faCalendarLinesPen as faCalendarLinesPenSolid,
  faClipboardMedical as faClipboardMedicalSolid,
  faComments as faCommentsSolid,
  faMessage as faMessageSolid,
  faSquareList as faSquareListSolid,
  faUser as faUserSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetPatientUnreadCommentsCountQuery } from "api/hooks/useGetPatientUnreadCommentsCountQuery";
import { useFeatureToggleContext } from "contexts/FeatureToggleContext";
import { useProfileContext } from "contexts/ProfileContext";
import { Hidden } from "shared/atoms/Hidden";
import { CurrentPatientContext } from "utils/contexts";
import { Feature } from "utils/features/types";
import { isUsingActivityTracking } from "utils/patient";
import { userIsTherapist } from "utils/profile/profileHelper";

import { MenuItem } from "./MenuItem";
import { useActiveLinkTranslation } from "./useActiveLinkTranslation";

export const PatientMenu: React.FC = () => {
  const { t } = useTranslation();
  const { patientId, ...rest } = useParams();
  const activeLink = Object.values(rest)?.[0]?.split("/")[0];
  const PATH = `/patients/${patientId}`;
  const { hasFeature } = useFeatureToggleContext();
  const activeLinkTranslation = useActiveLinkTranslation(activeLink);
  const { profile } = useProfileContext();
  const { patient } = useContext(CurrentPatientContext);

  invariant(profile, "Profile should be defined");
  invariant(patient, "Patient should be defined");
  const isTherapist = userIsTherapist(profile);

  const { data } = useGetPatientUnreadCommentsCountQuery(Number(patientId), {
    enabled: Boolean(patientId),
  });

  return (
    <>
      <Container data-testid="patients-menu">
        <MenuItem
          to={`${PATH}/overview`}
          label={t("patients.menu.overview")}
          icon={faUser}
          iconActive={faUserSolid}
          dataTestId="overview-tab-button"
        />

        {hasFeature(Feature.PATIENT_MESSAGES) && (
          <Hidden type="aboveTablet">
            <MenuItem
              to={`/messages/patients/${patientId}`}
              label={t("patients.menu.messages")}
              icon={faMessage}
              iconActive={faMessageSolid}
            />
          </Hidden>
        )}

        {hasFeature(Feature.PATIENT_COMMENTS) && (
          <MenuItem
            to={`${PATH}/comments`}
            label={t("patients.menu.comments")}
            icon={faComments}
            iconActive={faCommentsSolid}
            notificationCount={data?.count}
            dataTestId="comments-tab-button"
          />
        )}

        {hasFeature(Feature.PATIENT_PROTOCOL) && (
          <MenuItem
            to={`${PATH}/protocol/current`}
            label={t("patients.menu.protocol")}
            icon={faSquareList}
            iconActive={faSquareListSolid}
            dataTestId="protocol-tab-button"
          />
        )}

        <MenuItem
          to={`${PATH}/information/journal`}
          label={t("patients.menu.information")}
          icon={faClipboardMedical}
          iconActive={faClipboardMedicalSolid}
          dataTestId="information-tab-button"
        />

        {isTherapist && isUsingActivityTracking(patient) && (
          <MenuItem
            to={`${PATH}/activity_log`}
            label={t("patients.menu.activity_log")}
            icon={faCalendarLinesPen}
            iconActive={faCalendarLinesPenSolid}
            dataTestId="activity-log-tab-button"
          />
        )}
      </Container>
      <Hidden type="aboveTablet">
        {/* FIXME: type translation */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <SubContainer data-testid="title">{t(activeLinkTranslation)}</SubContainer>
      </Hidden>
    </>
  );
};

const Container = styled.div`
  height: 46px;
  display: flex;
  flex: 1;
  flex-direction: row;
  padding: 16px 16px 24px;
  background: ${props => props.theme.colors.white};
  border-radius: 12px 12px 0 0;
`;

const SubContainer = styled.div`
  display: flex;
  min-height: 32px;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  color: ${props => props.theme.colors.primary.base};
  ${props => props.theme.font.sub_menu.title};
  background: ${props => props.theme.colors.white};
`;
